import { startTransition, useDeferredValue, useEffect, useMemo, useRef, useState } from "react"
import Icon from "../../Icon"
import api from "../../../utils/api"

const ROLE_LABELS = {
  M: "Management",
  L: "Landlord",
  T: "Tenant",
  Mbr: "Member",
  A: "Admin",
}

const toScoreText = (value) =>
  Number.isFinite(Number(value)) ? `${Number(value).toFixed(1)}%` : "--"

const formatCount = (value) => (Number.isFinite(Number(value)) ? String(Number(value)) : "--")

const getStatusBadgeClass = (value = "") => {
  const normalized = String(value || "").toLowerCase()
  if (normalized === "excellent") return "bg-emerald-50 text-emerald-700 border-emerald-200"
  if (normalized === "good") return "bg-sky-50 text-sky-700 border-sky-200"
  if (normalized === "fair") return "bg-amber-50 text-amber-700 border-amber-200"
  if (normalized === "risk") return "bg-orange-50 text-orange-700 border-orange-200"
  if (normalized === "default") return "bg-red-50 text-red-700 border-red-200"
  return "bg-gray-50 text-gray-600 border-gray-200"
}

const matchesUserSearch = (user, query) => {
  const search = String(query || "").trim().toLowerCase()
  if (!search) return true

  const final = user?.payscore?.final || {}
  const frp = user?.payscore?.modules?.frp || {}
  const group = user?.payscore?.modules?.group || {}

  return [
    user?.name,
    user?.email,
    user?.phone,
    user?.idNumber,
    user?.role,
    ROLE_LABELS[user?.role] || user?.role,
    final?.status,
    frp?.status,
    group?.status,
    ...(Array.isArray(frp?.units) ? frp.units.flatMap((unit) => [unit?.name, unit?.propertyName]) : []),
    ...(Array.isArray(group?.groups) ? group.groups.map((entry) => entry?.name) : []),
  ].some((value) => String(value || "").toLowerCase().includes(search))
}

const hasAnyPayScoreData = (user) => {
  const final = user?.payscore?.final || {}
  const frp = user?.payscore?.modules?.frp || {}
  const group = user?.payscore?.modules?.group || {}
  return (
    Number.isFinite(Number(final?.score)) ||
    Number.isFinite(Number(frp?.score)) ||
    Number.isFinite(Number(group?.score)) ||
    (Array.isArray(final?.sourceModules) && final.sourceModules.length > 0)
  )
}

const hasFrpData = (user) => {
  const frp = user?.payscore?.modules?.frp || {}
  return Number.isFinite(Number(frp?.score)) || Number(frp?.currentUnitCount || 0) > 0
}

const hasGroupData = (user) => {
  const group = user?.payscore?.modules?.group || {}
  return (
    Number.isFinite(Number(group?.score)) ||
    Number(group?.currentOutstandingBalance || 0) > 0
  )
}

const sortUsersByScore = (users = [], scoreGetter) =>
  [...users].sort((left, right) => {
    const leftScore = Number(scoreGetter(left))
    const rightScore = Number(scoreGetter(right))
    const leftHasScore = Number.isFinite(leftScore)
    const rightHasScore = Number.isFinite(rightScore)

    if (leftHasScore && rightHasScore && rightScore !== leftScore) return rightScore - leftScore
    if (leftHasScore !== rightHasScore) return leftHasScore ? -1 : 1
    return String(left?.name || "").localeCompare(String(right?.name || ""))
  })

function AdminPayScoreModuleSection({ onImpersonateUser }) {
  const [collapsed, setCollapsed] = useState(true)
  const [finalCollapsed, setFinalCollapsed] = useState(true)
  const [forRentCollapsed, setForRentCollapsed] = useState(true)
  const [groupsCollapsed, setGroupsCollapsed] = useState(true)

  const [showCounts, setShowCounts] = useState({ final: 5, frp: 5, group: 5 })
  const [searchInput, setSearchInput] = useState("")
  const deferredSearch = useDeferredValue(searchInput)

  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryLoaded, setSummaryLoaded] = useState(false)
  const [summaryError, setSummaryError] = useState("")

  const [users, setUsers] = useState([])
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [detailsLoaded, setDetailsLoaded] = useState(false)
  const [detailsRefreshing, setDetailsRefreshing] = useState(false)
  const [detailsError, setDetailsError] = useState("")
  const detailsPollTimeoutRef = useRef(null)
  const detailsPollAttemptsRef = useRef(0)

  const shouldLoadDetails = !collapsed && (!finalCollapsed || !forRentCollapsed || !groupsCollapsed)

  const loadSummary = async () => {
    try {
      setSummaryLoading(true)
      setSummaryError("")
      const res = await api.get("/payscore/admin/summary")
      setSummary(res?.data?.summary || null)
      setSummaryLoaded(true)
    } catch (error) {
      console.error("Failed to load admin payscore summary", error)
      setSummaryError(error?.response?.data?.error || "Failed to load PaySCORE summary.")
      setSummaryLoaded(true)
    } finally {
      setSummaryLoading(false)
    }
  }

  const loadDetails = async (forceRefresh = false) => {
    try {
      setDetailsLoading(true)
      setDetailsError("")
      const suffix = forceRefresh ? "?refresh=1" : ""
      const res = await api.get(`/payscore/admin/overview${suffix}`)
      setUsers(Array.isArray(res?.data?.users) ? res.data.users : [])
      if (res?.data?.summary) setSummary(res.data.summary)
      setDetailsLoaded(true)

      const isRefreshing = !!res?.data?.refreshing
      setDetailsRefreshing(isRefreshing)
      if (isRefreshing && detailsPollAttemptsRef.current < 6) {
        detailsPollAttemptsRef.current += 1
        if (detailsPollTimeoutRef.current) {
          clearTimeout(detailsPollTimeoutRef.current)
        }
        detailsPollTimeoutRef.current = setTimeout(() => {
          loadDetails(false)
        }, 1200)
      } else if (!isRefreshing) {
        detailsPollAttemptsRef.current = 0
        if (detailsPollTimeoutRef.current) {
          clearTimeout(detailsPollTimeoutRef.current)
          detailsPollTimeoutRef.current = null
        }
      }
    } catch (error) {
      console.error("Failed to load admin payscore details", error)
      setDetailsError(error?.response?.data?.error || "Failed to load PaySCORE.")
      setDetailsRefreshing(false)
      setDetailsLoaded(true)
    } finally {
      setDetailsLoading(false)
    }
  }

  useEffect(() => {
    if (!summaryLoaded && !summaryLoading) loadSummary()
  }, [summaryLoaded, summaryLoading])

  useEffect(() => {
    if (shouldLoadDetails && !detailsLoaded && !detailsLoading) {
      loadDetails(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoadDetails, detailsLoaded, detailsLoading])

  useEffect(() => {
    setShowCounts({ final: 5, frp: 5, group: 5 })
  }, [deferredSearch])

  useEffect(() => {
    return () => {
      if (detailsPollTimeoutRef.current) {
        clearTimeout(detailsPollTimeoutRef.current)
      }
    }
  }, [])

  const searchedUsers = useMemo(() => users.filter((user) => matchesUserSearch(user, deferredSearch)), [users, deferredSearch])
  const usersWithData = useMemo(() => searchedUsers.filter((user) => hasAnyPayScoreData(user)), [searchedUsers])

  const finalUsers = useMemo(
    () => sortUsersByScore(usersWithData, (user) => user?.payscore?.final?.score),
    [usersWithData]
  )
  const frpUsers = useMemo(
    () => sortUsersByScore(searchedUsers.filter((user) => hasFrpData(user)), (user) => user?.payscore?.modules?.frp?.score),
    [searchedUsers]
  )
  const groupUsers = useMemo(
    () => sortUsersByScore(searchedUsers.filter((user) => hasGroupData(user)), (user) => user?.payscore?.modules?.group?.score),
    [searchedUsers]
  )

  const frpDefaultCount = useMemo(
    () => frpUsers.filter((user) => !!user?.payscore?.modules?.frp?.hasCurrentDefault).length,
    [frpUsers]
  )
  const groupDefaultCount = useMemo(
    () => groupUsers.filter((user) => !!user?.payscore?.modules?.group?.hasCurrentDefault).length,
    [groupUsers]
  )

  const usersScoredText = detailsLoaded ? formatCount(finalUsers.length) : formatCount(summary?.scoredUsersCount)
  const frpDefaultsText = detailsLoaded ? formatCount(frpDefaultCount) : formatCount(summary?.frpDefaultCount)
  const groupDefaultsText = detailsLoaded ? formatCount(groupDefaultCount) : formatCount(summary?.groupDefaultCount)
  const loadedUsersText = detailsLoaded ? formatCount(users.length) : formatCount(summary?.totalUsers)

  const handleRefresh = async () => {
    detailsPollAttemptsRef.current = 0
    if (detailsPollTimeoutRef.current) {
      clearTimeout(detailsPollTimeoutRef.current)
      detailsPollTimeoutRef.current = null
    }
    await loadSummary()
    if (detailsLoaded || shouldLoadDetails) {
      await loadDetails(true)
    }
  }

  const renderUserActions = (userId, destination = "/dashboard") => (
    <button
      onClick={() => onImpersonateUser?.(userId, { destination })}
      className="px-2.5 py-1 rounded bg-emerald-600 text-white text-[10px] font-semibold uppercase hover:bg-emerald-700 transition-colors"
    >
      View
    </button>
  )

  const renderFinalCard = (user) => {
    const final = user?.payscore?.final || {}
    const frp = user?.payscore?.modules?.frp || {}
    const group = user?.payscore?.modules?.group || {}
    const roleLabel = ROLE_LABELS[user?.role] || user?.role || "User"

    return (
      <div key={`final-${user.id}`} className="bg-white p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h6 className="text-sm font-semibold text-gray-900 truncate">{user.name || "Unknown user"}</h6>
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-tight">{roleLabel}</span>
              {user.locked && <span className="text-[8px] font-bold uppercase bg-red-50 text-red-600 px-1 rounded border border-red-100">Blocked</span>}
            </div>
            <p className="text-[10px] text-gray-400 font-medium truncate">{user.email || user.phone || user.idNumber || "No contact details"}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="text-right">
              <p className="text-[10px] font-bold text-gray-900">{toScoreText(final?.score)}</p>
              <span className={`text-[9px] font-bold uppercase tracking-tight ${getStatusBadgeClass(final?.status).split(' ').pop()}`}>{final?.status || "N/A"}</span>
            </div>
            {renderUserActions(user.id, "/dashboard")}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-4 gap-2">
          <div className="bg-gray-50/50 rounded p-1.5 border border-gray-100">
            <p className="text-[8px] text-gray-400 uppercase font-semibold">FRP</p>
            <p className="text-[10px] font-semibold text-gray-700">{toScoreText(frp?.score)}</p>
          </div>
          <div className="bg-gray-50/50 rounded p-1.5 border border-gray-100">
            <p className="text-[8px] text-gray-400 uppercase font-semibold">Groups</p>
            <p className="text-[10px] font-semibold text-gray-700">{toScoreText(group?.score)}</p>
          </div>
          <div className="bg-gray-50/50 rounded p-1.5 border border-gray-100">
            <p className="text-[8px] text-gray-400 uppercase font-semibold">Cap</p>
            <p className={`text-[10px] font-bold ${final?.capApplied ? "text-amber-600" : "text-gray-400"}`}>{final?.capApplied ? "YES" : "NO"}</p>
          </div>
          <div className="bg-gray-50/50 rounded p-1.5 border border-gray-100">
            <p className="text-[8px] text-gray-400 uppercase font-semibold">Default</p>
            <p className={`text-[10px] font-bold ${final?.hasCurrentDefault ? "text-red-600" : "text-emerald-600"}`}>{final?.hasCurrentDefault ? "YES" : "NO"}</p>
          </div>
        </div>
      </div>
    )
  }

  const renderFrpCard = (user) => {
    const frp = user?.payscore?.modules?.frp || {}

    return (
      <div key={`frp-${user.id}`} className="bg-white p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h6 className="text-sm font-semibold text-gray-900 truncate">{user.name || "Unknown user"}</h6>
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-tight">{ROLE_LABELS[user?.role] || user?.role || "User"}</span>
            </div>
            <p className="text-[10px] text-gray-400 font-medium truncate">{user.email || user.phone || user.idNumber || "No contact details"}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="text-right">
              <p className="text-[10px] font-bold text-emerald-600">{toScoreText(frp?.score)}</p>
              <span className={`text-[9px] font-bold uppercase tracking-tight ${getStatusBadgeClass(frp?.status).split(' ').pop()}`}>{frp?.status || "N/A"}</span>
            </div>
            {renderUserActions(user.id, "/app/frp")}
          </div>
        </div>
      </div>
    )
  }

  const renderGroupCard = (user) => {
    const group = user?.payscore?.modules?.group || {}

    return (
      <div key={`group-${user.id}`} className="bg-white p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h6 className="text-sm font-semibold text-gray-900 truncate">{user.name || "Unknown user"}</h6>
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-tight">{ROLE_LABELS[user?.role] || user?.role || "User"}</span>
            </div>
            <p className="text-[10px] text-gray-400 font-medium truncate">{user.email || user.phone || user.idNumber || "No contact details"}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="text-right">
              <p className="text-[10px] font-bold text-cyan-600">{toScoreText(group?.score)}</p>
              <span className={`text-[9px] font-bold uppercase tracking-tight ${getStatusBadgeClass(group?.status).split(' ').pop()}`}>{group?.status || "N/A"}</span>
            </div>
            {renderUserActions(user.id, "/app/groups")}
          </div>
        </div>
      </div>
    )
  }

  const renderSection = ({
    title,
    icon,
    collapsedState,
    setCollapsedState,
    rows,
    renderRow,
    showKey,
    subtitle,
    fallbackCount,
  }) => {
    const visibleRows = rows.slice(0, showCounts[showKey])
    const countLabel = detailsLoaded ? rows.length : fallbackCount
    const showRowsLoading = rows.length === 0 && (!detailsLoaded || detailsLoading || detailsRefreshing)

    return (
      <div className="bg-white overflow-hidden">
        <button
          onClick={() => setCollapsedState((prev) => !prev)}
          className="w-full flex items-center justify-between px-6 py-2.5 text-left hover:bg-gray-50 transition-colors group/cat"
        >
          <div className="flex items-center gap-2.5">
            <Icon name={icon} size="0.8rem" className="text-gray-400 group-hover/cat:text-emerald-600 transition-colors" />
            <span className="text-[11px] font-bold text-gray-700 uppercase tracking-tight group-hover/cat:text-gray-900">{title}</span>
            <span className="text-[10px] font-bold text-gray-400">
              ({countLabel})
            </span>
          </div>
          <Icon name={collapsedState ? "chevron-right" : "chevron-down"} size="0.8rem" className="text-gray-400 transition-transform duration-200" />
        </button>

        {!collapsedState && (
          <div className="divide-y divide-gray-100 bg-gray-50/20 border-t border-gray-100">
            {showRowsLoading ? (
              <div className="p-4 text-center text-[11px] text-gray-400 uppercase font-bold tracking-widest">
                {detailsLoading || detailsRefreshing ? "Loading..." : "Expand to Load"}
              </div>
            ) : rows.length === 0 ? (
              <div className="p-4 text-center text-[11px] text-gray-400 font-medium">No users found.</div>
            ) : (
              <>
                <div className="divide-y divide-gray-100 bg-white">
                  {visibleRows.map((row) => renderRow(row))}
                </div>
                <div className="p-2 flex justify-center gap-2">
                  {rows.length > showCounts[showKey] && (
                    <button
                      onClick={() => setShowCounts((prev) => ({ ...prev, [showKey]: prev[showKey] + 5 }))}
                      className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide hover:underline"
                    >
                      + {rows.length - showCounts[showKey]} More
                    </button>
                  )}
                  {showCounts[showKey] > 5 && (
                    <button
                      onClick={() => setShowCounts((prev) => ({ ...prev, [showKey]: 5 }))}
                      className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide hover:underline"
                    >
                      Less
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setCollapsed((prev) => !prev)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-100">
            <Icon name="bar-chart-line" size="1.25rem" className="text-white" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-gray-900 uppercase tracking-tight">PaySCORE Management</h4>
            <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Overall • Rent • Groups Scoring</p>
          </div>
        </div>
        <Icon name={collapsed ? "chevron-right" : "chevron-down"} size="1rem" className="text-gray-300 transition-transform duration-200" />
      </button>

      {!collapsed && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          {/* Search and Quick Stats */}
          <div className="p-4 bg-gray-50/30 space-y-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="search" size="0.8rem" className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search users, roles, groups..."
                value={searchInput}
                onChange={(event) => {
                  const nextValue = event.target.value
                  startTransition(() => setSearchInput(nextValue))
                }}
                className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 bg-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-gray-400 uppercase font-bold tracking-wider">Scored</p>
                <p className="text-xs font-bold text-gray-900">{usersScoredText}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-red-400 uppercase font-bold tracking-wider">FRP Defaults</p>
                <p className="text-xs font-bold text-red-600">{frpDefaultsText}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-red-400 uppercase font-bold tracking-wider">Grp Defaults</p>
                <p className="text-xs font-bold text-red-600">{groupDefaultsText}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-gray-400 uppercase font-bold tracking-wider">Total Users</p>
                <p className="text-xs font-bold text-gray-900">{loadedUsersText}</p>
              </div>
            </div>

            {(summaryError || detailsError) && (
              <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-[10px] text-red-700 font-medium">
                {summaryError || detailsError}
              </div>
            )}

            <button
              onClick={handleRefresh}
              disabled={summaryLoading || detailsLoading}
              className="w-full flex items-center justify-center gap-2 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors"
            >
              <Icon name="arrow-clockwise" size="0.75rem" />
              Refresh Data
            </button>
          </div>

          {renderSection({
            title: "RL PaySCORE",
            icon: "speedometer2",
            collapsedState: finalCollapsed,
            setCollapsedState: setFinalCollapsed,
            rows: finalUsers,
            renderRow: renderFinalCard,
            showKey: "final",
            subtitle: "Overall score across active score sources",
            fallbackCount: formatCount(summary?.scoredUsersCount),
          })}

          {renderSection({
            title: "ForRent PaySCORE",
            icon: "house",
            collapsedState: forRentCollapsed,
            setCollapsedState: setForRentCollapsed,
            rows: frpUsers,
            renderRow: renderFrpCard,
            showKey: "frp",
            subtitle: "Rent payment score and current FRP risk state",
            fallbackCount: formatCount(summary?.frpUsersCount),
          })}

          {renderSection({
            title: "Groups PaySCORE",
            icon: "people",
            collapsedState: groupsCollapsed,
            setCollapsedState: setGroupsCollapsed,
            rows: groupUsers,
            renderRow: renderGroupCard,
            showKey: "group",
            subtitle: "Contribution score and current group debt state",
            fallbackCount: formatCount(summary?.groupUsersCount),
          })}
        </div>
      )}
    </div>
  )
}

export default AdminPayScoreModuleSection
